/**
 * Build the project snapshot written after QuoteMaker has received and
 * validated a server-rendered PDF. Keeping this pure makes the save/reopen
 * contract testable without mounting the full Stage 5 editor.
 */
export function buildPdfGeneratedProjectUpdate(
    project,
    detail = {},
    eventAt = new Date().toISOString(),
    updatedAt = eventAt,
) {
    if (!project || typeof project !== 'object') {
        throw new TypeError('A project is required to record PDF generation');
    }

    const filename = typeof detail.filename === 'string' ? detail.filename.trim() : '';
    const savedPath = typeof detail.savedPath === 'string' ? detail.savedPath.trim() : '';
    const source = typeof detail.source === 'string' ? detail.source.trim() : '';
    const parsedBytes = detail.bytes === null || detail.bytes === undefined || detail.bytes === ''
        ? null
        : Number(detail.bytes);
    const bytes = Number.isSafeInteger(parsedBytes) && parsedBytes >= 0 ? parsedBytes : null;

    return {
        ...project,
        pdfGenerated: true,
        pdfGeneratedAt: eventAt,
        updatedAt,
        updated_at: updatedAt,
        aiContext: {
            ...(project.aiContext || {}),
            ...(detail.aiContext && typeof detail.aiContext === 'object' ? detail.aiContext : {}),
            lastQuotePdfGeneratedAt: eventAt,
            ...(filename ? { lastQuotePdfFilename: filename } : {}),
            // An omitted path means this generation was not written to the
            // synced folder. Clear any older path so the newest event cannot
            // misleadingly point at a previous PDF.
            lastQuotePdfSavedPath: savedPath || null,
            ...(source ? { lastQuotePdfSource: source } : {}),
            ...(bytes !== null ? { lastQuotePdfBytes: bytes } : {}),
        },
    };
}

/**
 * Record that the browser print hand-off was opened without treating a lean
 * project-history summary as a complete project envelope.
 */
export function buildPrintOpenedProjectUpdate(
    project,
    eventAt = new Date().toISOString(),
    updatedAt = eventAt,
) {
    if (!project || typeof project !== 'object') {
        throw new TypeError('A project is required to record the print hand-off');
    }

    return {
        ...project,
        pdfPrintOpened: true,
        pdfPrintOpenedAt: eventAt,
        updatedAt,
        updated_at: updatedAt,
        aiContext: {
            ...(project.aiContext || {}),
            lastQuotePrintOpenedAt: eventAt,
        },
    };
}

/**
 * Record the immutable scope snapshot that was handed to the email flow. The
 * caller owns the scope projection; this helper only applies the lifecycle
 * metadata while preserving every unrelated field on the cached project.
 */
export function buildEmailSentProjectUpdate(
    project,
    finalSentScope,
    eventAt = new Date().toISOString(),
    updatedAt = eventAt,
) {
    if (!project || typeof project !== 'object') {
        throw new TypeError('A project is required to record email delivery');
    }
    if (!Array.isArray(finalSentScope)) {
        throw new TypeError('A final sent scope array is required to record email delivery');
    }

    return {
        ...project,
        emailSent: true,
        emailSentAt: eventAt,
        updatedAt,
        updated_at: updatedAt,
        aiContext: {
            ...(project.aiContext || {}),
            finalSentScope,
            finalSentAt: eventAt,
        },
    };
}

const FINAL_SENT_SCOPE_MEDIA_FIELDS = new Set([
    'images',
    'photos',
    'assignedPhotos',
    'selectedPhotos',
    'attachments',
]);

/**
 * Build the immutable, server-safe scope corpus from the quote snapshot that
 * Stage 5 actually rendered when the user confirmed send. Preserve all copy,
 * pricing-basis, cost-breakdown, method and strategic fields; omit only bulky
 * media payloads that do not describe the sent scope and can exceed the audit
 * endpoint's bounded JSON size.
 */
export function buildFinalSentScopeFromQuoteSnapshot(quoteSnapshot) {
    if (!quoteSnapshot || !Array.isArray(quoteSnapshot.scope)) return [];
    return quoteSnapshot.scope
        .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
        .map((item) => {
            const auditItem = {};
            for (const [key, value] of Object.entries(item)) {
                if (FINAL_SENT_SCOPE_MEDIA_FIELDS.has(key) || typeof value === 'function') continue;
                auditItem[key] = value;
            }
            // JSON round-trip produces an immutable value graph suitable for
            // transport and prevents later Stage 5 state changes from altering
            // the already-confirmed corpus snapshot.
            return JSON.parse(JSON.stringify(auditItem));
        });
}
